import { useQuery } from '@tanstack/react-query';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { shipmentTypesenseService } from '~/services/shipment-typesense-service';
import { adaptUIParamsToTypesenseForFacets } from '~/utils/shipmentSearch/typesense-adapters';
import type { TypesenseSearchResponse } from '~/types/typesense-search';

interface FacetFilters {
  startDate: string;
  endDate: string;
  facetFilters?: {
    test_mode_facet?: string;
    destination_country_code_facet?: string;
    ship_option_facet?: string;
    carrier_service_method_id_facet?: string;
    shipment_type_facet?: string;
    tenant_id_facet?: string;
    origin_id_facet?: string;
    fulfillment_context_id_facet?: string;
  };
}

export const useTypesenseFacets = (baseFilters: FacetFilters, enabled: boolean) => {
  // Query for overall facet counts (without applied facet filters)
  const overallFilters = {
    ...baseFilters,
    facetFilters: undefined, // Remove facet filters to get overall counts
  };

  const overallQueryOptions = useQueryCreator(shipmentTypesenseService.fetchFacets, {
    args: [adaptUIParamsToTypesenseForFacets(overallFilters)],
  });

  const overallQuery = useQuery<TypesenseSearchResponse>({
    ...overallQueryOptions,
    queryKey: [...overallQueryOptions.queryKey, 'overall'],
    enabled,
    staleTime: 10000,
  });

  // Query for filtered facet counts (with applied facet filters)
  const filteredQueryOptions = useQueryCreator(shipmentTypesenseService.fetchFacets, {
    args: [adaptUIParamsToTypesenseForFacets(baseFilters)],
  });

  const filteredQuery = useQuery<TypesenseSearchResponse>({
    ...filteredQueryOptions,
    queryKey: [...filteredQueryOptions.queryKey, 'filtered'],
    enabled,
    staleTime: 10000,
  });

  return {
    overallData: overallQuery.data,
    filteredData: filteredQuery.data,
    isLoading: overallQuery.isLoading || filteredQuery.isLoading,
    error: overallQuery.error || filteredQuery.error,
    overallFacetCounts: overallQuery.data?.facet_counts,
    filteredFacetCounts: filteredQuery.data?.facet_counts,
  };
};
